import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { HiEye, HiEyeSlash, HiMoon, HiSun } from 'react-icons/hi2'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../contexts/ThemeContext'
import AdSagaLogo from '../assets/adsaga_logo.svg'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const { login, loading, isAuthenticated, error, clearAuthError } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/models')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    clearAuthError()
  }, [clearAuthError])

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const result = await login(formData.email, formData.password)
    if (result.type === 'auth/loginAdmin/fulfilled') {
      navigate('/admin/models')
    }
  }

  return (
    <div className="relative flex min-h-screen bg-app dark:bg-app-dark">
      <button
        type="button"
        onClick={toggleTheme}
        className="absolute right-4 top-4 z-10 rounded-lg p-2 text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800"
        aria-label={isDark ? 'Light mode' : 'Dark mode'}
      >
        {isDark ? <HiSun className="h-5 w-5" /> : <HiMoon className="h-5 w-5" />}
      </button>

      <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 p-12 text-white lg:flex">
        <div>
          <img src={AdSagaLogo} alt="" className="mb-8 h-14 w-14 rounded-xl bg-white/10 p-2 object-contain" />
          <h1 className="text-4xl font-bold tracking-tight">AdSaga.ai</h1>
          <p className="mt-2 text-lg text-primary-100">Administration console</p>
        </div>
        <p className="max-w-md text-sm leading-relaxed text-primary-100/90">
          Manage models, billing, organisations, and platform configuration from one secure workspace.
        </p>
      </div>

      <div className="flex w-full flex-1 items-center justify-center px-4 py-12 lg:w-1/2">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8 text-center lg:hidden">
            <img src={AdSagaLogo} alt="AdSaga" className="mx-auto h-16 w-16 object-contain" />
          </div>

          <div className="card">
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Sign in</h2>
              <p className="mt-1 text-sm text-text-muted dark:text-text-muted-dark">Use your administrator credentials</p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@company.com"
                required
                autoComplete="email"
              />

              <div>
                <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pr-10"
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-secondary-500 hover:text-secondary-700"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <HiEyeSlash className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in to admin panel'}
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-text-muted dark:text-text-muted-dark">
              Need access? Contact your system administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
